// What an inline card (a render plugin's output) may need from the page that
// hosts the chat, and the plugin render context does not carry: which customer
// the chat belongs to, who is looking, who owns the chat, and a way to put
// text in the composer.
//
// The plugin seam hands `render` only {state, toolCallId, sessionId}, and a
// card draws from its event payload alone. A card that ACTS (the warehouse
// query card re-runs a query, saves it, offers it to the composer) needs these
// few facts about the viewer. The chat page provides them; with no provider
// (a story, a test, the design showcase before it opts in) a card sees the
// empty default and is read-only.
import { createContext, useContext, type MutableRefObject } from 'react'
import type { AgentComposerHandle } from './plugins.js'
import { NOBODY_KNOWN, type WhoToAsk } from '../utils/permissionReason'

export interface AgentCardHost {
  /** The customer the chat belongs to (the :customer in /agent/:customer/...). */
  customer?: string
  /** Who is looking, as signed in. */
  viewerEmail?: string
  /** The viewer holds the Manager role. */
  viewerIsManager: boolean
  /** The chat's owner. Empty until the page has read it. */
  sessionOwner?: string
  /** Who to ask about an action the viewer cannot do. */
  whoToAsk: WhoToAsk
  /** The live composer handle: `setInput` replaces the unsent text. Null with no composer. */
  composerHandleRef?: MutableRefObject<AgentComposerHandle | null>
  /** Open the chat's Notebook on this cell (#1869). Absent: cards show no "Open in notebook". */
  openNotebook?: (cellId: string) => void
}

export const DEFAULT_CARD_HOST: AgentCardHost = { viewerIsManager: false, whoToAsk: NOBODY_KNOWN }

export const AgentCardHostContext = createContext<AgentCardHost>(DEFAULT_CARD_HOST)

export const useAgentCardHost = (): AgentCardHost => useContext(AgentCardHostContext)

/** The viewer is the chat's owner (emails compare case-insensitively). */
export const viewerOwnsChat = (host: AgentCardHost): boolean =>
  !!host.viewerEmail && !!host.sessionOwner && host.viewerEmail.toLowerCase() === host.sessionOwner.toLowerCase()
